import { useMemo, useState } from "react";
import { PlusIcon } from "@heroicons/react/outline";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import useSetQueryParams from "routes/club/hooks/useSetQueryParams";
import { useGetList } from "routes/club/hooks/useGetList";
import { useCreate } from "routes/club/hooks/useCreate";
import { FailedRowType, useCreateMass } from "routes/club/hooks/useCreateMass";
import { useEdit } from "routes/club/hooks/useEdit";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import useWindowWidth from "utills/InitWidthDevice";
import { isHaveActionPermission } from "utills/helpers";
import { DownloadIcon } from "@heroicons/react/outline";
import { downloadTemplate } from "routes/club/utils/helper";
import FilterBar, { FilterType } from "routes/club/components/FilterBar";
import DataTable from "routes/club/components/DataTable";
import FormModal, { formEmptyValue } from "routes/club/components/FormModal";
import {
  ClubType,
  ClubPayload,
  PagingType,
  ClubMassPayload,
} from "routes/club/types";
import LogHistoryModal from "routes/club/components/LogHistoryModal";

const ClubListPage = () => {
  const TEMPLATE_FILE_PATH = "/files/club_import_template.xlsx";
  const TEMPLATE_FILE_NAME = "club_import_template.xlsx";
  const PAGE_SIZE = 10;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const setQueryParams = useSetQueryParams();
  const windowWidth = useWindowWidth();

  const paramsObject = Object.fromEntries(searchParams.entries());

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const searchQuery = searchParams.get("q") ?? "";
  const is_active = searchParams.get("is_active") ?? "";

  const { data, loading, paging, params, setParams, fetchData } = useGetList({
    page: page,
    q: searchQuery,
    status: is_active,
    limit: PAGE_SIZE,
  });

  const { mutation: createMutation, loading: createLoading } = useCreate();
  const { mutation: createMassMutation, loading: createMassLoading } =
    useCreateMass();
  const { mutation: editMutation, loading: editLoading } = useEdit();

  // const formatPaging: PagingType = useMemo(() => {
  //   if (!paging) return null;

  //   return {
  //     current_page: paging?.current_page,
  //     total_item: paging?.total_item,
  //     total_page: paging?.total_page,
  //     total: paging?.total,
  //   };
  // }, [paging]) as PagingType;

  const [filter, setFilter] = useState<FilterType>({
    page: page,
    q: searchQuery,
    is_active: is_active,
  });
  const [openFormModal, setOpenFormModal] = useState<boolean>(false);
  const [openLogHistoryModal, setOpenLogHistoryModal] =
    useState<boolean>(false);
  const [targetClub, setTargetClub] = useState<ClubType | null>(null);

  const handleDowloadTemplate = () => {
    downloadTemplate(TEMPLATE_FILE_PATH, TEMPLATE_FILE_NAME);
  };

  const handleRefetchClubList = () => {
    fetchData(params);
  };

  const handleResetFetch = () => {
    console.log("Refetch API");

    // Reset filter
    setFilter({
      page: 1,
      q: "",
      is_active: "",
    });

    // Reset URL params
    setSearchParams({
      page: "1",
      q: "",
      is_active: "",
    });

    // Refetch API params
    fetchData({
      page: 1,
      q: "",
      status: "",
      limit: PAGE_SIZE,
    });
  };

  const handleChangeFilter = (key: string, value: string) => {
    setFilter({
      ...filter,
      [key]: value,
    });
  };

  const handleChangePage = (page: number) => {
    setFilter({ ...filter, page: page });
    setSearchParams({ ...paramsObject, page: page.toString() }); // Update URL search params
    setParams({ ...params, page: page }); // Update API params
  };

  const handleSearch = async (data: FilterType) => {
    console.log("Search:", data);

    // Update URL search params
    setSearchParams({
      ...paramsObject,
      page: "1",
      q: filter.q,
      is_active: filter.is_active,
    });

    // Update API search params
    setParams({
      ...params,
      page: 1,
      q: filter.q,
      status: filter.is_active,
    });
  };

  const handleUpdateTarget = (value: ClubType | null) => {
    setTargetClub(value);
  };

  const handleOpenFormModal = () => {
    setOpenFormModal(true);
  };

  const handleCloseFormModal = () => {
    setOpenFormModal(false);
    setTimeout(() => {
      setTargetClub(null); // Reset target club when close modal
    }, 500);
  };

  const handleSubmitModalForm = async (
    value: ClubPayload | ClubMassPayload[],
    mode: "create" | "create-mass" | "edit",
    resetFormApi: any,
    handleFormatMassImportFailed: (data: FailedRowType[]) => void,
    handleMassReset: () => void
  ) => {
    console.log("Submit form:", { mode, value });

    // Create mode
    if (mode === "create") {
      const res = await createMutation(value as ClubPayload);

      if (res.success) {
        toast.success("Create club successfully");
        handleCloseFormModal();
        resetFormApi(formEmptyValue);
        handleMassReset();
        handleResetFetch();
      }
    }

    // Create mass mode
    if (mode === "create-mass") {
      const res = await createMassMutation(value as ClubMassPayload[]);

      if (res.failed_rows?.length === 0) {
        toast.success("Create club mass successfully");
        handleCloseFormModal();
        resetFormApi(formEmptyValue);
        handleMassReset();
        handleResetFetch();
      } else if (res.total_success !== 0 && res.total_failed !== 0) {
        toast(
          `Total success: ${res.total_success}\nTotal failed: ${res.total_failed}`
        );
        handleFormatMassImportFailed(res.failed_rows);
      } else {
        toast.error("Create club mass failed");
        handleFormatMassImportFailed(res.failed_rows);
      }
    }

    // Edit mode
    if (mode === "edit") {
      const payload = value as ClubPayload;

      const res = await editMutation(payload?.id ?? "", payload);

      if (res.success) {
        toast.success("Edit club successfully");
        handleCloseFormModal();
        resetFormApi(formEmptyValue);
        handleRefetchClubList();
      }
    }
  };

  const handleOpenLogHistoryModal = () => {
    setOpenLogHistoryModal(true);
  };

  const handleCloseLogHistoryModal = () => {
    setOpenLogHistoryModal(false);
    setTargetClub(null); // Reset target club when close modal
  };

  return (
    <section className="wrapper">
      {loading && <FullScreenLoading />}

      {/* Form Modal */}
      <FormModal
        open={openFormModal}
        loading={createLoading || createMassLoading || editLoading}
        targetClub={targetClub}
        handleClose={handleCloseFormModal}
        handleSubmitModalForm={handleSubmitModalForm}
      />

      {/* Log History Modal */}
      {targetClub && (
        <LogHistoryModal
          open={openLogHistoryModal}
          targetClub={targetClub}
          handleClose={handleCloseLogHistoryModal}
        />
      )}

      <Breadcrumbs
        customPageLabel="Club"
        additionalLabel={
          <div className="flex gap-x-3">
            <Button
              type="button"
              className="w-11 h-11 bg-white rounded-md px-3 text-brand-500 py-2
                            border-solid border-brand-500 border-[1px]
                            hover:bg-white active:bg-white active:opacity-85 sm:w-auto"
              onClick={handleDowloadTemplate}
            >
              <DownloadIcon className="size-4 text-brand-500" />{" "}
              <span className="hidden sm:block sm:ml-2">
                Download Template File
              </span>
            </Button>

            {isHaveActionPermission("club", "other", "create") && (
              <Button
                className="w-11 h-11 sm:w-auto"
                onClick={() => {
                  handleOpenFormModal();
                }}
              >
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Add New Club</span>
              </Button>
            )}
          </div>
        }
      />

      {/* Action Bar */}
      <FilterBar
        filter={filter}
        handleChangeFilter={handleChangeFilter}
        onSubmit={handleSearch}
      />

      {/* Datatable */}
      <DataTable
        data={data}
        paging={paging}
        handleChangePage={handleChangePage}
        handleUpdateTarget={handleUpdateTarget}
        handleOpenFormModal={handleOpenFormModal}
        handleOpenLogHistoryModal={handleOpenLogHistoryModal}
        handleRefetchClubList={handleRefetchClubList}
      />
    </section>
  );
};

export default ClubListPage;
